import { Box, Typography } from "@mui/material";
import { useLeaderboards } from "api/leaderboards";
import ProgressIndicator from "components/ProgressIndicator/ProgressIndicator";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useNavigate, useParams } from "react-router-dom";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { LEADERBOARD_RANKING_LIST_ID } from "state/tables/types";
import { LeaderboardRanking } from "types/leaderboards/ranking";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { useStyles } from "./styles";

const TopRanking = () => {
  const navigate = useNavigate();
  const { message } = useMessage();
  const { listing, updateListing } = useListing(LEADERBOARD_RANKING_LIST_ID);
  const { setColumnOrdering } = useTablesStateHelpers();
  const { id: leaderboardId } = useParams<{ id: string }>();
  const { useLeaderboardRanking } = useLeaderboards();
  const { data: rankingData } = useLeaderboardRanking(leaderboardId || "", {
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
  });
  const classes = useStyles();

  const headItems = createHeadItems([
    {
      headerText: message({ id: "position" }),
      property: "position",
      isSortable: false,
    },
    {
      headerText: message({ id: "fullName" }),
      property: "firstName",
      isSortable: false,
    },
    {
      headerText: message({ id: "phoneNumber" }),
      property: "phoneNumber",
      isSortable: false,
    },
    {
      headerText: message({ id: "loyaltyCardNumber" }),
      property: "loyaltyCardNumber",
    },
    {
      headerText: message({ id: "promotionDate" }),
      property: "promotionDate",
      isSortable: false,
    },
    {
      headerText: message({ id: "score" }),
      property: "score",
      isSortable: false,
      align: "right",
    },
  ]);

  const createItems = (items: LeaderboardRanking[]): Row[] => {
    return items.map((item) => ({
      cells: [
        {
          content: (
            <Box className={classes.positionContainer}>
              <Typography className={classes.position}>
                {item.ranking.position}
              </Typography>
              <ProgressIndicator progress={item.ranking.progress.general} />
            </Box>
          ),
          property: "position",
        },
        {
          content: `${item.member.firstName || ""} ${item.member.lastName || ""}`,
          property: "firstName",
          dataHjSuppress: true,
        },
        {
          content: item.member.phone,
          property: "phoneNumber",
          dataHjSuppress: true,
        },
        {
          content: item.member.loyaltyCardNumber,
          property: "loyaltyCardNumber",
          dataHjSuppress: true,
        },
        {
          content: OLDate(item.ranking.changedPositionAt),
          property: "promotionDate",
          dataHjSuppress: true,
        },
        {
          content: item.ranking.score,
          property: "score",
          dataHjSuppress: true,
          align: "right",
        },
      ],
      id: item.member.customerId,
    }));
  };

  const items = rankingData?.items ? createItems(rankingData.items) : [];

  return (
    <OLTable
      fullView
      columns={rankingData && headItems}
      rows={rankingData && items}
      onSort={() => {}}
      sort={{
        order: listing.order || "asc",
        property: listing.property,
        page: listing.page,
        rowsPerPage: listing.rowsPerPage,
      }}
      tableName={LEADERBOARD_RANKING_LIST_ID}
      actions={{
        body: [
          {
            action: (id) =>
              navigate(links.singleMemberGeneralInfo(id as string)),
            label: message({ id: "seeFullProfile" }),
          },
        ],
      }}
      setColumnOrdering={setColumnOrdering}
      onRowClick={(id) => navigate(links.singleMemberGeneralInfo(id as string))}
      pagination={{
        count: calculateCount(
          rankingData?.total.filtered,
          rankingData?.total.all,
        ),
        rowsPerPage: listing.rowsPerPage,
        page: listing.page,
        onChangePage: (page) => {
          updateListing({
            type: ListingActions.CHANGE_PAGE,
            payload: page,
          });
        },
        onChangeRowsPerPage: (rows) => {
          updateListing({
            type: ListingActions.CHANGE_ROWS_PER_PAGE,
            payload: rows,
          });
        },
      }}
    />
  );
};

export default TopRanking;
